/** The Orders tab: every order, newest first, filtered by where it stands. */

import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Notice } from "@/components/ui/Field";
import { cn } from "@/lib/cn";
import { Amount } from "@/features/receipts/ReceiptBits";
import { OrderStatusBadge, DueDate } from "./OrderBits";
import { STATUSES, type OrderStatus } from "./lifecycle";
import { ordersQuery } from "./queries";

export function OrdersPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const orders = useInfiniteQuery(ordersQuery(status));
  const items = orders.data?.pages.flatMap((page) => page.items) ?? [];

  const chip = (selected: boolean) =>
    cn(
      "min-h-9 shrink-0 rounded-full border px-3 text-sm font-medium",
      selected
        ? "border-brand-700 bg-brand-700 text-white"
        : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50",
    );

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-stone-900">{t("orders.title")}</h1>
        <Link
          to="/orders/new"
          className="bg-brand-700 hover:bg-brand-800 inline-flex min-h-11 items-center gap-1.5 rounded-lg px-4 text-base font-medium text-white"
        >
          <Plus className="h-5 w-5" aria-hidden />
          {t("orders.add")}
        </Link>
      </header>

      <Link
        to="/orders/customers"
        className="text-brand-700 inline-flex items-center gap-1.5 text-sm underline"
      >
        <Users className="h-4 w-4" aria-hidden />
        {t("orders.customers.title")}
      </Link>

      {/* Scrolls sideways on a narrow phone rather than wrapping into three rows. */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        <button
          type="button"
          aria-pressed={status === null}
          className={chip(status === null)}
          onClick={() => setStatus(null)}
        >
          {t("orders.all")}
        </button>
        {STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={status === value}
            className={chip(status === value)}
            onClick={() => setStatus(value)}
          >
            {t(`orders.status.${value}`)}
          </button>
        ))}
      </div>

      {orders.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {orders.isError && <Notice tone="error">{t("orders.errors.loadFailed")}</Notice>}
      {orders.isSuccess && items.length === 0 && (
        <Notice tone="info">{status ? t("orders.noneWithStatus") : t("orders.empty")}</Notice>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {items.map((order) => (
            <li key={order.id}>
              <Link
                to="/orders/$orderId"
                params={{ orderId: order.id }}
                className="flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="truncate font-medium text-stone-800">{order.description}</p>
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-500">
                    <span className="truncate">{order.customer.name}</span>
                    <DueDate date={order.due_date} />
                    <OrderStatusBadge status={order.status} />
                  </p>
                </div>
                <Amount money={order.quoted_total} className="font-medium text-stone-800" />
                <ChevronRight className="h-5 w-5 shrink-0 text-stone-300" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {orders.hasNextPage && (
        <Button
          variant="secondary"
          disabled={orders.isFetchingNextPage}
          onClick={() => void orders.fetchNextPage()}
        >
          {orders.isFetchingNextPage ? t("common.loading") : t("orders.loadMore")}
        </Button>
      )}
    </section>
  );
}
